import React, {
  ButtonHTMLAttributes,
  FC,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { FormButton } from './Form';
import { PrimaryButton } from './Primary';
import { SecondaryButton } from './Secondary';
import { DropdownButton } from './Dropdown';
import { TextLinks } from './TextLink';

export type btnLevel = 1 | 2 | 3 | undefined;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  items?: string[];
  level?: btnLevel;
  buttonType?: 'outlined' | 'dropdown' | 'visited';
  variant: 'primary' | 'secondary' | 'form' | 'text';
  textVariant?: 'light' | 'dark';
  setSelectedItem?: Dispatch<SetStateAction<string>>;
}

export const Button: FC<ButtonProps> = ({
  children,
  className = '',
  disabled,
  items = [],
  level,
  buttonType,
  variant,
  textVariant,
  setSelectedItem,
  ...rest
}: ButtonProps) => {
  /* Checking for missing required props */
  if (!variant)
    throw new Error(
      `You need to pass a prop called "variant" of type string to the Button component. To learn more about the list of required props for the
      Button component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-buttons`
    );
  /* Checking for missing required props */
  const renderPrimaryDefault = variant === 'primary' &&
    !buttonType &&
    !level && (
      <PrimaryButton
        variant="default"
        className={className && className}
        disabled={disabled}
        {...rest}
      >
        {children}
      </PrimaryButton>
    );

  const renderPrimaryOutlined = variant === 'primary' &&
    buttonType === 'outlined' &&
    !level && (
      <PrimaryButton
        variant="outlined"
        className={className && className}
        disabled={disabled}
        {...rest}
      >
        {children}
      </PrimaryButton>
    );

  const renderSecondary = variant === 'secondary' && !buttonType && !level && (
    <SecondaryButton
      variant="default"
      className={className && className}
      disabled={disabled}
      {...rest}
    >
      {children}
    </SecondaryButton>
  );

  const renderSecondaryOutlined = variant === 'secondary' &&
    buttonType === 'outlined' &&
    !level && (
      <SecondaryButton
        variant="outlined"
        className={className && className}
        disabled={disabled}
        {...rest}
      >
        {children}
      </SecondaryButton>
    );

  const renderSecondaryDropdown = variant === 'secondary' &&
    buttonType === 'dropdown' &&
    !level && (
      <DropdownButton
        items={items}
        className={className && className}
        setSelectedItem={setSelectedItem}
        {...rest}
      >
        {children}
      </DropdownButton>
    );

  const renderFormPrimary = variant === 'form' &&
    !buttonType &&
    level === 1 && (
      <FormButton
        variant="default"
        className={className && className}
        disabled={disabled}
        {...rest}
      >
        {children}
      </FormButton>
    );

  const renderFormSecondary = variant === 'form' &&
    !buttonType &&
    level === 2 && (
      <FormButton
        variant="secondary"
        className={className && className}
        disabled={disabled}
        {...rest}
      >
        {children}
      </FormButton>
    );

  const renderFormThird = variant === 'form' && !buttonType && level === 3 && (
    <FormButton
      variant="tertiary"
      className={className && className}
      disabled={disabled}
      {...rest}
    >
      {children}
    </FormButton>
  );

  const renderTextLinksPrimaryDefault = variant === 'text' &&
    !buttonType &&
    level === 1 && (
      <TextLinks
        fontCase="normal-case"
        disabled={disabled}
        className={className && className}
        textVariant={textVariant}
        {...rest}
      >
        {children}
      </TextLinks>
    );

  const renderTextLinksPrimaryUppercase = variant === 'text' &&
    !buttonType &&
    level === 2 && (
      <TextLinks
        fontCase="uppercase"
        disabled={disabled}
        className={className && className}
        textVariant={textVariant}
        {...rest}
      >
        {children}
      </TextLinks>
    );

  return (
    <>
      {renderPrimaryDefault}
      {renderPrimaryOutlined}
      {renderSecondary}
      {renderSecondaryDropdown}
      {renderFormPrimary}
      {renderFormSecondary}
      {renderFormThird}
      {renderTextLinksPrimaryDefault}
      {renderTextLinksPrimaryUppercase}
      {renderSecondaryOutlined}
    </>
  );
};
